import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log, error as logError } from '../utils/DebugLogger';
import { validate } from '../contracts/validate';
import '../contracts/ServicePriorities_SalvarInput.ts';
import '../contracts/ServicePriorities_RemoverInput.ts';
import '../contracts/ServicePriorities_ClonarInput.ts';

import '../contracts/ServicePriorities_GetPriceResponse';
import '../contracts/ServicePriorities_GetResponse';
import '../contracts/ServicePriorities_ListaPorFranquiaResponse';
import '../contracts/ServicePriorities_ListaPorServicoResponse';
import '../contracts/ServicePriorities_ListaResponse';
import '../contracts/ServicePriorities_MatrixResponse';
import '../contracts/ServicePriorities_RemoverResponse';
import '../contracts/ServicePriorities_SalvarResponse';
import '../contracts/ServicePriorities_ClonarResponse';
import type { ServicePriorities_RemoverInput } from '../contracts/ServicePriorities_RemoverInput';
import type { ServicePriorities_SalvarInput } from '../contracts/ServicePriorities_SalvarInput';
import type { ServicePriorities_ClonarInput } from '../contracts/ServicePriorities_ClonarInput';
/**
 * Composable for SO Service Priority matrix operations.
 * Manages service-priority relationships and pricing.
 */
export function useSOServicePriorities() {
  const isLoading = ref(false);
  const error = ref(null);
  const basePath = '/index.php/so/service_priorities';

  const fetchAll = async () => {
    log('useSOServicePriorities', 'fetchAll', 'Fetching all service priorities');
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`${basePath}/lista`, { responseContract: 'ServicePriorities_ListaResponse' });
      if (response.success) {
        return response.dados || [];
      }
      throw new Error(response.error || 'Erro ao buscar prioridades de serviço');
    } catch (err) {
      logError('useSOServicePriorities', 'fetchAll', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const getById = async (id: any) => {
    log('useSOServicePriorities', 'getById', 'Fetching service priority', { id });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`${basePath}/get/${id}`, { responseContract: 'ServicePriorities_GetResponse' });
      if (response.success) {
        return response.dados;
      }
      throw new Error(response.error || 'Erro ao buscar prioridade de serviço');
    } catch (err) {
      logError('useSOServicePriorities', 'getById', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const save = async (data: ServicePriorities_SalvarInput) => {
    log('useSOServicePriorities', 'save', 'Saving service priority', data);
    isLoading.value = true;
    error.value = null;
    try {
      validate('ServicePriorities_SalvarInput', data);
      const response = await httpService.post(`${basePath}/salvar`, data, { responseContract: 'ServicePriorities_SalvarResponse' });
      if (response.success) {
        return response;
      }
      throw new Error(response.error || 'Erro ao salvar prioridade de serviço');
    } catch (err) {
      logError('useSOServicePriorities', 'save', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const remove = async (data: ServicePriorities_RemoverInput) => {
    log('useSOServicePriorities', 'remove', 'Removing service priority', data);
    isLoading.value = true;
    error.value = null;
    try {
      validate('ServicePriorities_RemoverInput', data);
      const response = await httpService.post(`${basePath}/remover`, data, { responseContract: 'ServicePriorities_RemoverResponse' });
      if (response.success) {
        return response;
      }
      throw new Error(response.error || 'Erro ao remover prioridade de serviço');
    } catch (err) {
      logError('useSOServicePriorities', 'remove', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const clone = async (payload: ServicePriorities_ClonarInput) => {
    log('useSOServicePriorities', 'clone', 'Cloning service priorities', payload);
    isLoading.value = true;
    error.value = null;
    try {
      validate('ServicePriorities_ClonarInput', payload);
      const response = await httpService.post(`${basePath}/clonar`, payload, { responseContract: 'ServicePriorities_ClonarResponse' });
      if (response.success) {
        return response;
      }
      throw new Error(response.error || 'Erro ao clonar prioridades de serviço');
    } catch (err) {
      logError('useSOServicePriorities', 'clone', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchMatrix = async () => {
    log('useSOServicePriorities', 'fetchMatrix', 'Fetching priority matrix');
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`${basePath}/matrix`, { responseContract: 'ServicePriorities_MatrixResponse' });
      if (response.success) {
        return response.dados || [];
      }
      throw new Error(response.error || 'Erro ao buscar matriz de prioridades');
    } catch (err) {
      logError('useSOServicePriorities', 'fetchMatrix', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const getPrice = async (serviceId: any, priorityId: any) => {
    log('useSOServicePriorities', 'getPrice', 'Fetching price', { serviceId, priorityId });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`${basePath}/get_price/${serviceId}/${priorityId}`, { responseContract: 'ServicePriorities_GetPriceResponse' });
      if (response.success) {
        return response.dados;
      }
      throw new Error(response.error || 'Erro ao buscar preço');
    } catch (err) {
      logError('useSOServicePriorities', 'getPrice', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchByService = async (serviceId: number) => {
    log('useSOServicePriorities', 'fetchByService', 'Fetching priorities by service', { serviceId });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`${basePath}/lista_por_servico/${serviceId}`, { responseContract: 'ServicePriorities_ListaPorServicoResponse' });
      if (response.success) {
        return response.dados || [];
      }
      throw new Error(response.error || 'Erro ao buscar prioridades por serviço');
    } catch (err) {
      logError('useSOServicePriorities', 'fetchByService', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchByFranchise = async (franchiseId: number) => {
    log('useSOServicePriorities', 'fetchByFranchise', 'Fetching by franchise', { franchiseId });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`${basePath}/lista_por_franquia/${franchiseId}`, { responseContract: 'ServicePriorities_ListaPorFranquiaResponse' });
      if (response.success) {
        return response.dados || [];
      }
      throw new Error(response.error || 'Erro ao buscar prioridades por franquia');
    } catch (err) {
      logError('useSOServicePriorities', 'fetchByFranchise', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    isLoading,
    error,
    fetchAll,
    getById,
    save,
    remove,
    clone,
    fetchMatrix,
    getPrice,
    fetchByService,
    fetchByFranchise
  };
}
